import gsap from 'gsap';
import { siteConfig } from '../common/common';

export enum STAnimationType {
	unidirectional = 'unidirectional',
	bidirectional = 'bidirectional',
	triggered = 'triggered',
}

export interface STAnimationParams {
	startProps?: any; // The start properties of the element
	endProps?: any; // The end properties of the element (applicable if manualUpdate is not provided)
	start: any; // The scroll startpoint (e.g. 30%)
	end: any; // The scroll endpoint (e.g. 100%)
	easing?: any; // Easing function for the animation
	duration?: number; // Duration of the animation (applicable to triggered type)
	skipIfPassedAtPageLoad?: boolean; // If true, the animation will be skipped-to-end if the element is already past the startPercent at page load
	scrollTarget?: HTMLElement; // If specified, use this element for the scroll triggering, and 'element' as the animation target
	manualUpdate?: (progress: number) => void; // Callback to manually update the element based on the progress
	onStart?: () => void; // Callback to run when the animation starts
}

export function rosAnimation(element: HTMLElement, type: STAnimationType, params: STAnimationParams): gsap.core.Tween | null {
	if (!siteConfig.enableRevealOnScroll) return null;
	return stAnimation(element, type, params);
}

export function stAnimation(element: HTMLElement, type: STAnimationType, params: STAnimationParams): gsap.core.Tween | null {
	const state = { manualProgress: 0 };
	let startAnchor = params.start;
	let endAnchor = params.end;
	// check if params.start is a function
	if (typeof params.start !== 'function' && params.start.indexOf(' ') == -1) startAnchor = `${params.start} 100%`;
	if (typeof params.end !== 'function' && params.end.indexOf(' ') == -1) endAnchor = `${params.end} 100%`;

	const ease = params.easing || 'power2.inOut';
	params.startProps ||= {};
	gsap.set(element, params.startProps);

	let tweenParams: any = {
		duration: params.duration || 1,
		ease: ease,
		onStart: params.onStart,
		scrollTrigger: {
			trigger: params.scrollTarget || element,
			start: `${startAnchor}`,
			end: `${endAnchor}`,
			scrub: type === STAnimationType.bidirectional || type === STAnimationType.unidirectional,
			//toggleActions: STAnimationType.unidirectional ? 'play none none none' : 'play none none reverse',
			toggleActions: 'play none none none',
		},
	};

	if (type === STAnimationType.triggered) {
		tweenParams = { ...tweenParams, ...params.endProps };
	}

	const tween = gsap.to(element, tweenParams);

	if (params.skipIfPassedAtPageLoad && type === STAnimationType.triggered && tween.scrollTrigger) {
		// Calculate how far the element is past window.innerHeight.
		// If it is greater than startPercent, set the tween progress to 1
		// and clean up the tween.
		requestAnimationFrame(() => {
			const rect = element.getBoundingClientRect();
			const elementTop = rect.top;
			const windowHeight = window.innerHeight;
			const elementHeight = element.clientHeight;
			const offset = windowHeight - elementTop;
			const offsetPercent = (offset / elementHeight) * 100;
			logger.log(offsetPercent, params.start);

			if (offsetPercent >= params.start) {
				tween?.progress(1);
				tween?.scrollTrigger?.kill();
				tween?.kill();
				return null;
			}
		});
	}

	// Define the onUpdate callback so that it can be defined in the scope
	// that gives it access to the Component, and reference the ScrollTrigger
	// progress via the tween reference instead of "this.scrollTrigger.progress".
	if (type !== STAnimationType.triggered) {
		// add an event for on scroll
		tween.eventCallback('onUpdate', () => {
			updateTween(tween, type, params, state, element);
		});
	}

	return tween;
}

function updateTween(tween: gsap.core.Tween, type: STAnimationType, params: STAnimationParams, state: any, element: HTMLElement) {
	//console.log('update', tween.progress());
	const scrollTrigger = tween.scrollTrigger as ScrollTrigger;
	if (scrollTrigger) {
		// Operate using a manual progress value to ensure
		// that the animation progress can't go in reverse.
		const p = scrollTrigger.progress;
		const bidirectional = type === STAnimationType.bidirectional;
		state.manualProgress = bidirectional ? scrollTrigger.progress : Math.max(p, state.manualProgress);
		if (params.endProps) {
			// for each property key in startProps that also exists in endProps, have gsap interpolate between the two values
			const keys = Object.keys(params.startProps);
			keys.forEach((key) => {
				if (params.endProps[key] != undefined) {
					const startValue = params.startProps[key];
					const endValue = params.endProps[key];
					const newValue = gsap.utils.interpolate(startValue, endValue, state.manualProgress);
					gsap.set(element, { [key]: newValue });
				}
			});
		} else {
			params.manualUpdate && params.manualUpdate(state.manualProgress);
		}
	}
}
